import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { useSelector } from 'react-redux';

import CouponCategoryField from 'containers/CouponCategoryField/CouponCategoryField';
import {
  AlertTriangle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Grid,
  Info as InfoIcon,
  Layers,
  Lock,
  Package,
  Plus,
  Scissors,
  Tag,
  Target,
  Users,
  X,
} from 'react-feather';


import styles from './Page2_BuildYourDeal.module.scss';

// ─── Constants ────────────────────────────────────────────────────────────────
const RED = '#c0392b';
const BORDER = '#e2d8d8';
const TOTAL_STEPS = 5;
const CURRENT_STEP = 2;

const TAX_SCHEMES = {
  NS: { hst: 0.15 }, NB: { hst: 0.15 }, NL: { hst: 0.15 },
  PE: { hst: 0.15 }, ON: { hst: 0.13 },
  AB: { gst: 0.05 }, NT: { gst: 0.05 }, NU: { gst: 0.05 }, YT: { gst: 0.05 },
  BC: { gst: 0.05, pst: 0.07 }, SK: { gst: 0.05, pst: 0.06 },
  MB: { gst: 0.05, rst: 0.07 }, QC: { gst: 0.05, qst: 0.09975 },
};

const ITEM_TYPES = [
  { key: 'individual', label: 'Individual item',  icon: Package, blurb: 'Spotlight a hero product/service to drive focused demand.' },
  { key: 'category',   label: 'Item categories',  icon: Layers,  blurb: 'Promote a collection (e.g., appetizers, skincare line, classes).' },
  { key: 'all',        label: 'All Products',      icon: Grid,    blurb: 'Great for storewide promos and broad acquisition pushes.' },
];

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday','Any Day'];
const TIME_BLOCKS = ['Morning (8–12)','Afternoon (1–6)','Evening (6–9)','Night (9–Closing)','Custom'];
const NEW_CUSTOMER_AUDIENCE = ['Students','Seniors','Families','Young Adults','Tourists','All Customers / Not Restricted'];
const SELL_MORE_AUDIENCE = ['Families','Young Adults','Students','Seniors','Budget-Conscious Shoppers','Tech Enthusiasts','Local Community Members','All Customers / Not Restricted'];
const EXCLUSIVITY_OPTIONS = [{ key: 'premium', label: 'Premium Members' },{ key: 'followers', label: 'Followers' }];

// ─── Responsive hook ──────────────────────────────────────────────────────────
const useWindowWidth = () => {
  const [width, setWidth] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  React.useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return width;
};

// ─── Small helpers ─────────────────────────────────────────────────────────────

function Tip({ msg }) {
  return (
    <span title={msg} style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 4, color: '#aaa', cursor: 'help' }}>
      <InfoIcon size={13} />
    </span>
  );
}

function Label({ children, red }) {
  return (
    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: red ? RED : '#374151', marginBottom: 6 }}>
      {children}
    </label>
  );
}

function Input({ style, ...props }) {
  return (
    <input style={{
      width: '100%', boxSizing: 'border-box',
      border: '1.5px solid #e5e7eb', borderRadius: 8,
      padding: '10px 12px', fontSize: 13, color: '#111',
      outline: 'none', background: '#fff',
      ...style,
    }} {...props} />
  );
}

function Textarea({ style, ...props }) {
  return (
    <textarea style={{
      width: '100%', boxSizing: 'border-box',
      border: '1.5px solid #e5e7eb', borderRadius: 8,
      padding: '10px 12px', fontSize: 13, color: '#111',
      outline: 'none', background: '#fff', resize: 'vertical',
      ...style,
    }} {...props} />
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '7px 14px', borderRadius: 999,
      border: active ? 'none' : '1.5px solid #d1d5db',
      background: active ? '#1a1a1a' : '#fff',
      color: active ? '#fff' : '#374151',
      fontSize: 13, fontWeight: 600, cursor: 'pointer',
      transition: 'background .15s',
      whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}

function BtnOutline({ children, onClick, style }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '9px 18px', borderRadius: 8,
      border: `1.5px solid ${RED}`, background: '#fff',
      color: RED, fontSize: 13, fontWeight: 600, cursor: 'pointer',
      ...style,
    }}>{children}</button>
  );
}

function BtnPrimary({ children, onClick, disabled, type = 'button', style }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '10px 24px', borderRadius: 8,
      border: 'none', background: disabled ? '#e5e7eb' : RED,
      color: disabled ? '#9ca3af' : '#fff',
      fontSize: 14, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
      ...style,
    }}>{children}</button>
  );
}

function Stepper({ value, onChange, min = 0, step = 1 }) {
  const n = parseFloat(value) || 0;
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', border: `1.5px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden', height: 40 }}>
      <input value={value} onChange={e => onChange(e.target.value)}
        style={{ flex: 1, border: 'none', padding: '0 10px', fontSize: 13, color: '#111', background: '#fff', minWidth: 0 }} />
      <div style={{ display: 'flex', flexDirection: 'column', borderLeft: `1px solid ${BORDER}` }}>
        <button type="button" onClick={() => onChange(Math.max(min, +(n - step).toFixed(4)))}
          style={{ flex: 1, width: 28, border: 'none', borderBottom: `1px solid ${BORDER}`, background: '#fff', cursor: 'pointer', color: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, lineHeight: 1 }}>−</button>
        <button type="button" onClick={() => onChange(+(n + step).toFixed(4))}
          style={{ flex: 1, width: 28, border: 'none', background: '#fff', cursor: 'pointer', color: RED, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, lineHeight: 1 }}>+</button>
      </div>
    </div>
  );
}

// ─── Promotion type cards ──────────────────────────────────────────────────────

function PromotionCards({ itemType, setItemType }) {
  const width = useWindowWidth();
  const isMobile = width < 641;
  const isTablet = width < 821;

  return (
    <div style={{
      background: '#fff', borderRadius: 16,
      padding: isMobile ? '18px 16px' : '24px 28px',
      marginBottom: 20,
      boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a', marginBottom: 20 }}>Promotion</div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2,1fr)' : 'repeat(3,1fr)',
        gap: 14,
      }}>
        {ITEM_TYPES.map(({ key, label, icon: Icon, blurb }) => {
          const active = itemType === key;
          return (
            <button key={key} type="button" onClick={() => setItemType(key)}
              style={{
                position: 'relative',
                background: active ? RED : '#fff',
                border: active ? 'none' : '1.5px solid #e5e7eb',
                borderRadius: 12,
                padding: isMobile ? '14px 12px' : '18px 16px 16px',
                cursor: 'pointer', textAlign: 'left',
                display: 'flex', flexDirection: isMobile ? 'row' : 'column', gap: isMobile ? 12 : 0,
                alignItems: isMobile ? 'center' : 'flex-start',
                boxShadow: active ? '0 4px 16px rgba(192,57,43,0.25)' : 'none',
                transition: 'background .15s',
                minHeight: isMobile ? 'auto' : 140,
              }}>
              <div style={{
                width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                background: active ? 'rgba(255,255,255,0.2)' : 'rgba(192,57,43,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: isMobile ? 0 : 14,
              }}>
                <Icon size={20} color={active ? '#fff' : RED} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: active ? '#fff' : '#1a1a1a', lineHeight: 1.3, marginBottom: isMobile ? 2 : 8 }}>
                  {label}
                </div>
                <div style={{ fontSize: 12, color: active ? 'rgba(255,255,255,0.8)' : '#9ca3af', lineHeight: 1.5 }}>
                  {blurb}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Image upload panel ───────────────────────────────────────────────────────

function ImagePanel({
  productImages, openCropWithURL, openCropWithFile,
  removeImageAt, maxImageSlots, selectedImageIdx, setSelectedImageIdx,
}) {
  const fallbackIdx = (() => {
    if (productImages[selectedImageIdx]) return selectedImageIdx;
    const firstFilled = productImages.findIndex(Boolean);
    return firstFilled === -1 ? 0 : firstFilled;
  })();

  const idx = fallbackIdx;
  const mainImg = productImages[idx] || '';

  const handleRemove = (i) => {
    removeImageAt(i);
    if (i === idx) {
      const next = productImages.map((v, j) => (j === i ? '' : v));
      const fallback = next.findIndex(Boolean);
      setSelectedImageIdx(fallback === -1 ? 0 : fallback);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Label red>
        Upload Images <Tip msg={`Upload up to ${maxImageSlots} images for this coupon. Click a thumbnail to preview it.`} />
      </Label>

      <div style={{ border: '1.5px solid #e5e7eb', borderRadius: 12, padding: 12, background: '#fff' }}>
        <div style={{ display: 'flex', gap: 12, overflow: 'hidden' }}>
          {mainImg ? (
            <img src={mainImg} style={{ flex: 1, minWidth: 0, width: '100%', height: 'auto', display: 'block', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb' }} />
          ) : (
            <div style={{
              flex: 1, position: 'relative', paddingBottom: '100%', borderRadius: 8,
              border: '1px solid #e5e7eb', overflow: 'hidden', background: '#f9fafb',
            }}>
              <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                <span style={{ color: '#9ca3af', fontSize: 12 }}>No image</span>
              </div>
            </div>
          )}

          {mainImg && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0, width: 100 }}>
              <button type="button" onClick={() => openCropWithURL(idx, mainImg)} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', borderRadius: 7,
                border: '1.5px solid #e5e7eb', background: '#fff',
                color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>
                <Scissors size={12} /> Crop
              </button>
              <label>
                <input type="file" accept="image/*" hidden onChange={e => {
                  const f = e.target.files?.[0]; e.target.value = '';
                  if (f) openCropWithFile(idx, f);
                }} />
                <span style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px', borderRadius: 7,
                  border: '1.5px solid #e5e7eb', background: '#fff',
                  color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>Replace</span>
              </label>
              <button type="button" onClick={() => handleRemove(idx)} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', borderRadius: 7,
                border: `1.5px solid ${RED}`, background: '#fff',
                color: RED, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>
                <X size={12} /> Remove
              </button>
            </div>
          )}
        </div>

        {!mainImg && (
          <label style={{ display: 'inline-block', marginTop: 8 }}>
            <input type="file" accept="image/*" hidden onChange={e => {
              const f = e.target.files?.[0]; e.target.value = '';
              if (f) openCropWithFile(idx, f);
            }} />
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 8,
              border: '1.5px solid #e5e7eb', background: '#fff',
              color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>Upload</span>
          </label>
        )}

        {maxImageSlots > 0 && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {Array.from({ length: maxImageSlots }).map((_, i) => {
              const url = productImages[i] || '';
              const isActive = i === idx;
              return url ? (
                <div key={i} style={{ position: 'relative', width: 52, height: 52 }}>
                  <div onClick={() => setSelectedImageIdx(i)} title="Click to preview"
                    style={{
                      width: 52, height: 52, borderRadius: 7, overflow: 'hidden',
                      border: isActive ? `2px solid ${RED}` : '1px solid #e5e7eb',
                      cursor: 'pointer',
                    }}>
                    <img src={url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <button type="button" onClick={(e) => { e.stopPropagation(); handleRemove(i); }}
                    aria-label={`Remove image ${i + 1}`}
                    style={{
                      position: 'absolute', top: -6, right: -6,
                      width: 18, height: 18, borderRadius: '50%',
                      border: '1px solid #e5e7eb', background: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', padding: 0,
                    }}>
                    <X size={11} color={RED} />
                  </button>
                </div>
              ) : (
                <label key={i} style={{
                  width: 52, height: 52, borderRadius: 7,
                  border: '1.5px dashed #d1d5db', background: '#f9fafb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}>
                  <input type="file" accept="image/*" hidden onChange={e => {
                    const f = e.target.files?.[0]; e.target.value = '';
                    if (f) openCropWithFile(i, f);
                  }} />
                  <Plus size={16} color="#9ca3af" />
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Product details panel ─────────────────────────────────────────────────────

function ProductDetailsPanel({
  itemType, goalKey,
  productTitle, setProductTitle,
  description, setDescription,
  price, setPrice, priceError, setPriceError,
  taxes, setTaxes, visibleTaxes, setVisibleTaxes,
  markEdited, businessProvince, priceNum,
  addTaxSelection, setAddTaxSelection,
  complements, addComplement, updateComplement, removeComplement,
  productImages, openCropWithURL, openCropWithFile, removeImageAt, maxImageSlots,
  selectedImageIdx, setSelectedImageIdx,
}) {
  const width = useWindowWidth();
  const isMobile = width < 641;

  const effectiveType = goalKey === 'Sell more' ? 'individual' : itemType;

  const titleLabel =
    effectiveType === 'individual' ? 'Main item name' :
    effectiveType === 'category'   ? 'Line Title' : 'Storewide Title';

  const descLabel =
    effectiveType === 'category' ? 'Line Description' : 'Description';

  const titlePlaceholder =
    effectiveType === 'individual' ? 'e.g., Espresso Latte' :
    effectiveType === 'category'   ? 'e.g., Summer Collection' : 'e.g., Everything in Store';

  const descPlaceholder =
    effectiveType === 'individual' ? 'Rich espresso with steamed milk and a silky microfoam.' :
    effectiveType === 'category'   ? 'Our full range of seasonal apparel.' : 'Describe your storewide promotion.';

  return (
    <div style={{
      background: '#fff', borderRadius: 16,
      padding: isMobile ? '18px 16px 16px' : '24px 28px 20px',
      boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
      marginBottom: 20,
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>Product / Offer details</div>
      <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 20 }}>Tell us what you're promoting and your business context.</div>

      {/* Responsive two-column on desktop, single-column on mobile */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? 16 : 24,
      }}>
        {/* LEFT */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <Label red>{titleLabel} <Tip msg="Primary name used in coupon title/description." /></Label>
            <Input value={productTitle} onChange={e => setProductTitle(e.target.value)} placeholder={titlePlaceholder} />
          </div>
          <div>
            <Label red>{descLabel} <Tip msg="Brief, compelling description for AI copy." /></Label>
            <Textarea rows={4} value={description} onChange={e => setDescription(e.target.value)} placeholder={descPlaceholder} />
          </div>

          <div>
            <Label red>Coupon Category <Tip msg="Select the category for your coupon." /></Label>
            <CouponCategoryField />
          </div>

          {effectiveType === 'individual' && (
            <div style={{ border: `1.5px solid ${BORDER}`, borderRadius: 10, padding: '14px' }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <button type="button"
                  onClick={() => {
                    const avail = ['gst','pst','qst','rst','hst'].find(k => !visibleTaxes.includes(k));
                    if (avail) setVisibleTaxes(v => [...v, avail]);
                  }}
                  style={{ padding: '6px 12px', border: `1.5px solid ${RED}`, borderRadius: 7, background: '#fff2f2', color: RED, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  + Add Tax
                </button>
                {visibleTaxes.length > 0 && (
                  <button type="button"
                    onClick={() => { setVisibleTaxes([]); setTaxes({ gst: '', pst: '', qst: '', rst: '', hst: '' }); }}
                    style={{ padding: '6px 12px', border: `1.5px solid ${BORDER}`, borderRadius: 7, background: '#fff', color: '#666', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    ✕ Remove
                  </button>
                )}
              </div>

              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: '#444' }}>Main item Price</span>
                  <Tip msg="Standard retail price; used for tax/discount calculations." />
                </div>
                <Stepper value={price} onChange={v => { setPrice(v); if (priceError) setPriceError(''); }} min={0} step={0.5} />
                {priceError && <div style={{ color: RED, fontSize: 12, marginTop: 4 }}>{priceError}</div>}
              </div>

              {visibleTaxes.map(key => (
                <div key={key} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: '#444' }}>{key.toUpperCase()}</span>
                    <Tip msg={`${key.toUpperCase()} tax amount`} />
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                    <div style={{ flex: 1 }}>
                      <Stepper
                        value={taxes[key] || ''}
                        onChange={v => { markEdited(key); setTaxes(t => ({ ...t, [key]: v })); }}
                        min={0} step={0.01}
                      />
                    </div>
                    <button type="button"
                      onClick={() => { setVisibleTaxes(v => v.filter(k => k !== key)); setTaxes(t => ({ ...t, [key]: '' })); }}
                      aria-label={`Remove ${key.toUpperCase()} tax`}
                      style={{
                        width: 40, height: 40, flexShrink: 0,
                        border: `1.5px solid ${BORDER}`, borderRadius: 8,
                        background: '#fff', color: RED, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ))}

              {['gst','pst','qst','rst','hst'].filter(k => !visibleTaxes.includes(k)).length > 0 && (
                <select
                  onChange={e => {
                    const k = e.target.value; if (!k) return;
                    setVisibleTaxes(v => [...v, k]);
                    setTaxes(t => ({ ...t, [k]: '' }));
                    e.target.value = '';
                  }}
                  style={{ width: '100%', padding: '7px 10px', border: `1.5px solid ${BORDER}`, borderRadius: 7, fontSize: 12, color: '#555', background: '#fff', marginTop: 2 }}>
                  <option value="">Add tax…</option>
                  {['gst','pst','qst','rst','hst'].filter(k => !visibleTaxes.includes(k)).map(k => (
                    <option key={k} value={k}>{k.toUpperCase()}</option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — image panel */}
        <ImagePanel
          productImages={productImages}
          openCropWithURL={openCropWithURL}
          openCropWithFile={openCropWithFile}
          removeImageAt={removeImageAt}
          maxImageSlots={maxImageSlots}
          selectedImageIdx={selectedImageIdx}
          setSelectedImageIdx={setSelectedImageIdx}
        />
      </div>

      {/* Complementary items */}
      {complements.map((c, i) => (
        <div key={i} style={{
          display: 'grid',
          gridTemplateColumns: width < 641 ? '1fr' : '1fr 1fr auto',
          gap: 12, marginTop: 12, alignItems: 'flex-end',
        }}>
          <div>
            <Label>Complementary Item Title</Label>
            <Input value={c.title || ''} onChange={e => updateComplement(i, { ...c, title: e.target.value })} placeholder="e.g., Butter Croissant" />
          </div>
          <div>
            <Label>Price (optional)</Label>
            <Input type="number" min="0" step="0.01" value={c.price ?? ''} onChange={e => updateComplement(i, { ...c, price: e.target.value })} placeholder="e.g., 3.50" />
          </div>
          <BtnOutline onClick={() => removeComplement(i)} style={width < 641 ? { width: '100%', justifyContent: 'center' } : {}}>Remove</BtnOutline>
        </div>
      ))}
    </div>
  );
}

// ─── Crop modal ────────────────────────────────────────────────────────────────

function CropModal({ cropImgURL, canvasRef, zoom, setZoom, offset, setOffset,
  baseScaleRef, handleCropMouseDown, handleCropMouseMove, handleCropMouseUp,
  handleWheel, applyCrop, onClose, drawToCanvas, CROP_W, CROP_H }) {

  return (
    <div role="dialog" aria-modal="true"
      onMouseUp={handleCropMouseUp} onMouseLeave={handleCropMouseUp}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(17,24,39,.55)',
        display: 'grid', placeItems: 'center', zIndex: 1000, padding: 16,
      }}>
      <div style={{
          width: 'min(100%,520px)',
          maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
          display: 'flex', flexDirection: 'column',
          background: '#fff', borderRadius: 16,
          border: '1px solid #e7eaf0', boxShadow: '0 20px 50px rgba(0,0,0,.25)', padding: 16,
        }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontWeight: 800, color: '#111827' }}>Crop Image (1:1)</div>
          <BtnOutline onClick={onClose}>Close</BtnOutline>
        </div>
        <div onMouseDown={handleCropMouseDown} onMouseMove={handleCropMouseMove} onWheel={handleWheel}
          style={{
            width: 'min(100%, 50vh)', margin: '0 auto', flexShrink: 0,
            aspectRatio: '1/1', borderRadius: 12, border: '1px solid #e7eaf0',
            overflow: 'hidden', background: '#f8fafc', display: 'grid', placeItems: 'center', userSelect: 'none',
          }}>
          <canvas ref={canvasRef} width={CROP_W} height={CROP_H}
            style={{ width: '100%', height: 'auto', aspectRatio: '1/1', objectFit: 'contain', display: 'block' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <label style={{ fontSize: 13, color: '#374151', minWidth: 50 }}>Zoom</label>
          <input type="range" min="1" max="3" step="0.01" value={zoom}
            onChange={e => setZoom(Number(e.target.value))} style={{ flex: 1 }} />
          <div style={{ width: 48, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{zoom.toFixed(2)}×</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
          <BtnOutline onClick={() => {
            const img = new Image();
            img.onload = () => {
              const s = Math.max(CROP_W / img.naturalWidth, CROP_H / img.naturalHeight);
              baseScaleRef.current = s;
              const dw = img.naturalWidth * s, dh = img.naturalHeight * s;
              setOffset({ x: (CROP_W - dw) / 2, y: (CROP_H - dh) / 2 });
              setZoom(1);
              drawToCanvas(img, s, 1, { x: (CROP_W - dw) / 2, y: (CROP_H - dh) / 2 });
            };
            img.src = cropImgURL;
          }}>Reset</BtnOutline>
          <BtnPrimary onClick={applyCrop}>Apply Crop</BtnPrimary>
        </div>
      </div>
    </div>
  );
}

// ─── Main export ───────────────────────────────────────────────────────────────

export default function Page2_BuildYourDeal({
  goal, onNext, onBack, onClose,
  initialValues = {},
  businessProvince = 'NS',
  isPremiumUser = false,
  allowedImageSlots,
}) {
  const width = useWindowWidth();
  const isMobile = width < 641;
  const pagePaddingH = isMobile ? 14 : width < 821 ? 24 : 40;

  const goalKey = useMemo(() => {
    if (!goal) return 'More Customers';
    const g = goal.toLowerCase();
    if (g.includes('more customers') || g.includes('attract')) return 'More Customers';
    if (g.includes('sell') || g.includes('increase')) return 'Sell more';
    if (g.includes('repeat')) return 'Drive Repeat Business';
    if (g.includes('slow') || g.includes('off-peak')) return 'Fill Slow Periods / Boost Off-Peak Sales';
    if (g.includes('special')) return 'Special Promotion';
    return goal;
  }, [goal]);

  const businessInfo = useSelector(s => s.businessInfo?.data || {});

  const [audience, setAudience] = useState(initialValues.audience || []);
  const [exclusiveTo, setExclusiveTo] = useState(initialValues.exclusiveTo || '');
  const [itemType, setItemType] = useState(initialValues.itemType || '');
  const [productTitle, setProductTitle] = useState(initialValues.product?.title || '');
  const [description, setDescription] = useState(initialValues.product?.description || '');
  const [price, setPrice] = useState(initialValues.product?.price == null ? '' : String(initialValues.product.price));
  const [priceError, setPriceError] = useState('');
  const [taxes, setTaxes] = useState({ gst: '', pst: '', qst: '', rst: '', hst: '' });
  const [visibleTaxes, setVisibleTaxes] = useState(() => Object.keys(TAX_SCHEMES[businessProvince] || {}));
  const userEdited = useRef(new Set());
  const markEdited = key => userEdited.current.add(key);
  const [complements, setComplements] = useState(initialValues.product?.complements || []);
  const didTouchAudienceRef = useRef(false);

  const maxImageSlots = 3;

  const initialImages = useMemo(() => {
    const imgs = Array.isArray(initialValues.product?.images) ? initialValues.product.images.filter(Boolean) : [];
    if (imgs.length > 0) return imgs.slice(0, maxImageSlots);
    if (initialValues.product?.imageUrl) return [initialValues.product.imageUrl];
    return [];
  }, [initialValues, maxImageSlots]);

  const [productImages, setProductImages] = useState(initialImages);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);

  const addOrReplaceImageAt = (index, url) => setProductImages(prev => {
    const next = [...prev];
    while (next.length < maxImageSlots) next.push('');
    let targetIndex = index;
    if (!next[index]) {
      const firstEmpty = next.findIndex(v => !v);
      if (firstEmpty !== -1) targetIndex = firstEmpty;
    }
    next[targetIndex] = url;
    return next.slice(0, maxImageSlots);
  });

  const removeImageAt = index => setProductImages(prev => {
    const next = [...prev]; next[index] = ''; return next;
  });

  const [cropOpen, setCropOpen] = useState(false);
  const [cropSlotIndex, setCropSlotIndex] = useState(0);
  const [cropImgURL, setCropImgURL] = useState('');
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const baseScaleRef = useRef(1);
  const canvasRef = useRef(null);
  const CROP_W = 1000, CROP_H = 1000;

  const openCropWithFile = (idx, file) => {
    const r = new FileReader();
    r.onload = e => openCropWithURL(idx, e.target.result);
    r.readAsDataURL(file);
  };
  const openCropWithURL = (idx, url) => { setCropSlotIndex(idx); setCropImgURL(url); setCropOpen(true); };

  const drawToCanvas = (imgObj, baseScale, zoomMul, off) => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = CROP_W; canvas.height = CROP_H;
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, CROP_W, CROP_H);
    const scale = baseScale * zoomMul;
    const dw = imgObj.naturalWidth * scale, dh = imgObj.naturalHeight * scale;
    const clamp = (o, dw, dh) => {
      let x = o.x, y = o.y;
      if (dw <= CROP_W) x = (CROP_W - dw) / 2; else x = Math.min(0, Math.max(CROP_W - dw, x));
      if (dh <= CROP_H) y = (CROP_H - dh) / 2; else y = Math.min(0, Math.max(CROP_H - dh, y));
      return { x, y };
    };
    const c = clamp(off, dw, dh);
    ctx.drawImage(imgObj, c.x, c.y, dw, dh);
  };

  useEffect(() => {
    if (!cropOpen || !cropImgURL) return;
    const img = new Image();
    img.onload = () => {
      const s = Math.max(CROP_W / img.naturalWidth, CROP_H / img.naturalHeight);
      baseScaleRef.current = s;
      const dw = img.naturalWidth * s, dh = img.naturalHeight * s;
      setOffset({ x: (CROP_W - dw) / 2, y: (CROP_H - dh) / 2 });
      setZoom(1);
      requestAnimationFrame(() => drawToCanvas(img, s, 1, { x: (CROP_W - dw) / 2, y: (CROP_H - dh) / 2 }));
    };
    img.src = cropImgURL;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropOpen, cropImgURL]);

  useEffect(() => {
    if (!cropOpen || !cropImgURL || !canvasRef.current) return;
    const img = new Image();
    img.onload = () => drawToCanvas(img, baseScaleRef.current, zoom, offset);
    img.src = cropImgURL;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, offset]);

  const handleCropMouseDown = e => { isDraggingRef.current = true; lastPosRef.current = { x: e.clientX, y: e.clientY }; };
  const handleCropMouseMove = e => {
    if (!isDraggingRef.current) return;
    const dx = e.clientX - lastPosRef.current.x, dy = e.clientY - lastPosRef.current.y;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
  };
  const handleCropMouseUp = () => { isDraggingRef.current = false; };
  const handleWheel = e => { e.preventDefault(); setZoom(z => Math.min(3, Math.max(1, z + (e.deltaY > 0 ? -0.05 : 0.05)))); };

  const applyCrop = () => {
    const canvas = canvasRef.current; if (!canvas) return;
    addOrReplaceImageAt(cropSlotIndex, canvas.toDataURL('image/jpeg', 0.8));
    setCropOpen(false);
    setSelectedImageIdx(cropSlotIndex);
  };

  const [slowDays, setSlowDays] = useState(initialValues.slowPeriods?.days || []);
  const [slowTimes, setSlowTimes] = useState(initialValues.slowPeriods?.timeBlocks || []);
  const [customTimeActive, setCustomTimeActive] = useState(() => (initialValues.slowPeriods?.timeBlocks || []).includes('Custom'));
  const [customFrom, setCustomFrom] = useState(initialValues.slowPeriods?.customTime?.from || '');
  const [customTo, setCustomTo] = useState(initialValues.slowPeriods?.customTime?.to || '');

  const [frequency, setFrequency] = useState(initialValues.repeatBusiness?.frequency || '');
  const [validDays, setValidDays] = useState(initialValues.repeatBusiness?.validDays || []);

  const audienceOptions = goalKey === 'Sell more' ? SELL_MORE_AUDIENCE : NEW_CUSTOMER_AUDIENCE;
  const priceNum = Number(price || 0) || 0;
  const [addTaxSelection, setAddTaxSelection] = useState('');

  useEffect(() => { if (goalKey === 'Sell more') setItemType('individual'); }, [goalKey]);

  useEffect(() => {
    const scheme = TAX_SCHEMES[businessProvince] || {};
    setVisibleTaxes(prev => Array.from(new Set([...prev, ...Object.keys(scheme)])));
    setTaxes(prev => {
      const next = { ...prev };
      Object.entries(scheme).forEach(([key, rate]) => {
        const computed = priceNum > 0 ? (Math.round(priceNum * rate * 100) / 100).toFixed(2) : '';
        if (next[key] === '' || !userEdited.current.has(key)) next[key] = computed === '0.00' ? '' : computed;
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceNum, businessProvince]);

  useEffect(() => {
    if (didTouchAudienceRef.current) return;
    const validOpts = goalKey === 'Sell more' ? SELL_MORE_AUDIENCE : NEW_CUSTOMER_AUDIENCE;
    const provided = Array.isArray(initialValues.audience) ? initialValues.audience : [];
    setAudience(provided.filter(v => validOpts.includes(v)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalKey]);

  const canContinueBase = useMemo(() => {
    const hasOffering = Boolean((itemType || (goalKey === 'Sell more' && 'individual')) && productTitle.trim());
    const numsOk = [price].every(v => v === '' || !Number.isNaN(Number(v)));
    const perTaxOk = visibleTaxes.every(k => { const v = taxes[k]; return v === '' || !Number.isNaN(Number(v)); });
    const customOk = !customTimeActive || (customFrom && customTo);
    return hasOffering && numsOk && perTaxOk && customOk;
  }, [goalKey, itemType, productTitle, price, taxes, visibleTaxes, customTimeActive, customFrom, customTo]);

  const handleContinue = formValues => {
    if (!canContinueBase) return;
    const effectiveType = goalKey === 'Sell more' ? 'individual' : itemType;
    if (effectiveType === 'individual' && price === '') { setPriceError('A price is required.'); return; }
    const imagesClean = productImages.filter(Boolean);
    const selectedCategoryObj = formValues?.category || null;
    const selectedSubcategories = formValues?.subcategories?.length ? formValues.subcategories : initialValues.product?.subcategories || [];

    const payload = {
      goal: goalKey, audience, exclusiveTo,
      itemType: effectiveType,
      product: {
        title: productTitle.trim(), description: description.trim(),
        price: price === '' ? null : Number(price),
        category: selectedCategoryObj || initialValues.product?.category,
        categoryId: selectedCategoryObj?.id || null, categoryName: selectedCategoryObj?.name || null,
        subcategoryId: selectedSubcategories?.[0] || null, subcategories: selectedSubcategories,
        complements: complements.map(c => ({ title: (c.title || '').trim(), price: c.price === '' ? null : Number(c.price) })),
        images: imagesClean, primaryImage: imagesClean[0] || '', thumbnail: imagesClean[0] || '',
        taxes: { gst: taxes.gst || '', pst: taxes.pst || '', qst: taxes.qst || '', rst: taxes.rst || '', hst: taxes.hst || '' },
      },
      constraints: { minSpend: null, purchaseQty: null },
      repeatBusiness: goalKey === 'Drive Repeat Business' ? { frequency, validDays } : null,
      slowPeriods: goalKey === 'Fill Slow Periods / Boost Off-Peak Sales' ? {
        days: slowDays, timeBlocks: slowTimes.filter(t => t !== 'Custom'),
        ...(customTimeActive && customFrom && customTo ? { customTime: { from: customFrom, to: customTo } } : {}),
      } : null,
    };
    if (goalKey === 'Sell more') payload.expiryDays = 30;
    if (initialValues.businessName || businessInfo.name) payload.businessName = initialValues.businessName || businessInfo.name;
    if (initialValues.city || businessInfo.city) payload.city = initialValues.city || businessInfo.city;
    payload.startFromScratch = goal === 'Start with No Goal In Mind';
    localStorage.setItem('cbv2_savedTaxes', JSON.stringify(payload.product.taxes));
    onNext?.(payload);
  };


  const nestedFormInitialValues = useMemo(() => ({
  category: initialValues.product?.category || undefined,
  subcategories: initialValues.product?.subcategories || initialValues.subcategories || [],
}), [initialValues.product?.category, initialValues.product?.subcategories, initialValues.subcategories]);

  return (
    <Form
      onSubmit={handleContinue}
      initialValues={nestedFormInitialValues}
      keepDirtyOnReinitialize
    >
      {({ handleSubmit, values }) => (
        <form onSubmit={handleSubmit} style={{
          minHeight: '100vh',
          background: 'linear-gradient(150deg,#f7e8e8 0%,#eddcdc 50%,#e6cdcd 100%)',
          fontFamily: "'Inter','Segoe UI',sans-serif",
          padding: '0 0 40px',
          boxSizing: 'border-box',
        }}>

          {/* ── Top bar ── */}
          <div style={{ padding: `20px ${pagePaddingH}px 0`, position: 'relative' }}>
            {/* Progress */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
                  const n = i + 1, isActive = n === CURRENT_STEP, isDone = n < CURRENT_STEP;
                  return (
                    <React.Fragment key={n}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        background: isActive || isDone ? RED : 'transparent',
                        border: isActive || isDone ? 'none' : '2px solid #ccc',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, color: isActive || isDone ? '#fff' : '#bbb',
                      }}>{n}</div>
                      {i < TOTAL_STEPS - 1 && <div style={{ flex: 1, height: 2, background: isDone ? RED : '#ddd' }} />}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            <h2 style={{
              margin: '0 0 6px',
              fontSize: isMobile ? 20 : width < 821 ? 22 : 24,
              fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.3px',
            }}>
              Build your Deal: Tell us about your Offering
            </h2>
            <p style={{ margin: '0 0 28px', fontSize: 13, color: '#888', lineHeight: 1.6, maxWidth: 560 }}>
              Tell us what you're promoting and your business context. We'll tailor high-performing deals.
            </p>
          </div>

          {/* ── Content ── */}
          <div style={{ padding: `0 ${pagePaddingH}px` }}>

            {goalKey !== 'Sell more' && (
              <PromotionCards itemType={itemType} setItemType={setItemType} />
            )}

            {(itemType || goalKey === 'Sell more') && (
              <ProductDetailsPanel
                itemType={itemType} goalKey={goalKey}
                productTitle={productTitle} setProductTitle={setProductTitle}
                description={description} setDescription={setDescription}
                price={price} setPrice={setPrice} priceError={priceError} setPriceError={setPriceError}
                taxes={taxes} setTaxes={setTaxes}
                visibleTaxes={visibleTaxes} setVisibleTaxes={setVisibleTaxes}
                markEdited={markEdited} businessProvince={businessProvince} priceNum={priceNum}
                addTaxSelection={addTaxSelection} setAddTaxSelection={setAddTaxSelection}
                complements={complements}
                addComplement={() => setComplements([...complements, { title: '', price: '' }])}
                updateComplement={(i, v) => setComplements(complements.map((c, idx) => idx === i ? v : c))}
                removeComplement={i => setComplements(complements.filter((_, idx) => idx !== i))}
                productImages={productImages}
                openCropWithURL={openCropWithURL} openCropWithFile={openCropWithFile}
                removeImageAt={removeImageAt} maxImageSlots={maxImageSlots}
                selectedImageIdx={selectedImageIdx} setSelectedImageIdx={setSelectedImageIdx}
              />
            )}

            {goalKey === 'Drive Repeat Business' && (
              <div style={{
                background: '#fff', borderRadius: 16,
                padding: isMobile ? '16px' : '20px 24px',
                marginBottom: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                  <Tag size={16} color={RED} />
                  <span style={{ fontSize: 14, fontWeight: 700 }}>How frequently do you want to encourage customers to return?</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                  {['Once a month','Multiple times a month','Seasonally / For special occasions'].map(f => (
                    <Chip key={f} active={frequency === f} onClick={() => setFrequency(f)}>{f}</Chip>
                  ))}
                </div>
                <Label>Encourage specific day(s) of the week?</Label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {DAYS.map(d => {
                    const active = validDays.includes(d);
                    return <Chip key={d} active={active} onClick={() => {
                      if (d === 'Any Day') { setValidDays(['Any Day']); return; }
                      const next = new Set(validDays.includes('Any Day') ? [] : validDays);
                      next.has(d) ? next.delete(d) : next.add(d); setValidDays([...next]);
                    }}>{d}</Chip>;
                  })}
                </div>
              </div>
            )}

            {goalKey === 'Fill Slow Periods / Boost Off-Peak Sales' && (
              <div style={{
                background: '#fff', borderRadius: 16,
                padding: isMobile ? '16px' : '20px 24px',
                marginBottom: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <Calendar size={16} color={RED} />
                  <span style={{ fontSize: 14, fontWeight: 700 }}>When are your typical slow periods or off-peak hours?</span>
                </div>
                <p style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 14px' }}>Select days and/or time blocks to pre-fill conditions later.</p>
                <Label>Days of week</Label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                  {DAYS.slice(0, 7).map(d => {
                    const active = slowDays.includes(d);
                    return <Chip key={d} active={active} onClick={() => {
                      const next = new Set(slowDays); next.has(d) ? next.delete(d) : next.add(d); setSlowDays([...next]);
                    }}>{d}</Chip>;
                  })}
                </div>
                <Label>Time blocks</Label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {TIME_BLOCKS.map(t => {
                    const isCustom = t === 'Custom';
                    const active = isCustom ? customTimeActive : slowTimes.includes(t);
                    return <Chip key={t} active={active} onClick={() => {
                      if (isCustom) { setCustomTimeActive(a => !a); if (customTimeActive) { setCustomFrom(''); setCustomTo(''); } }
                      else { const next = new Set(slowTimes); next.has(t) ? next.delete(t) : next.add(t); setSlowTimes([...next]); }
                    }}>{t}</Chip>;
                  })}
                </div>
                {customTimeActive && (
                  <div style={{
                    display: 'flex', gap: 12, alignItems: 'center', marginTop: 12,
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                  }}>
                    <Input type="time" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                      style={{ width: isMobile ? '100%' : 140 }} />
                    <span style={{ color: '#6b7280', flexShrink: 0 }}>to</span>
                    <Input type="time" value={customTo} onChange={e => setCustomTo(e.target.value)}
                      style={{ width: isMobile ? '100%' : 140 }} />
                  </div>
                )}
              </div>
            )}

            {/* ── Nav buttons ── */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 8,
              gap: 12,
              flexWrap: 'wrap',
            }}>
              <BtnOutline onClick={onBack} style={{ gap: 6 }}>
                <ChevronLeft size={15} /> Back
              </BtnOutline>
              <BtnPrimary type="submit" disabled={!canContinueBase}>
                Next <ChevronRight size={15} />
              </BtnPrimary>
            </div>
          </div>

          {cropOpen && (
            <CropModal
              cropImgURL={cropImgURL} canvasRef={canvasRef}
              zoom={zoom} setZoom={setZoom} offset={offset} setOffset={setOffset}
              baseScaleRef={baseScaleRef}
              handleCropMouseDown={handleCropMouseDown} handleCropMouseMove={handleCropMouseMove}
              handleCropMouseUp={handleCropMouseUp} handleWheel={handleWheel}
              applyCrop={applyCrop} onClose={() => setCropOpen(false)}
              drawToCanvas={drawToCanvas} CROP_W={CROP_W} CROP_H={CROP_H}
            />
          )}
        </form>
      )}
    </Form>
  );
}